import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Hero from './Hero';
import sectionsData from '../data/sections.json';

interface LifeMetadata {
  music: {
    instruments: string[];
    genres: string[];
    influences?: string[];
  };
  photography: {
    equipment: string[];
    styles: string[];
    influences?: string[];
  };
  media: {
    video: string[];
    twitch: string[];
  };
  travel?: {
    countries: string[];
    bucketList: string[];
  };
}

interface Category {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}

const Life: React.FC = () => {
  const [metadata, setMetadata] = useState<LifeMetadata | null>(null);

  useEffect(() => {
    const lifeSection = sectionsData.sections.find(section => section.id === 'life');
    console.log('Life section data:', lifeSection);
    if (lifeSection && lifeSection.metadata) {
      setMetadata(lifeSection.metadata as unknown as LifeMetadata);
    }
  }, []);

  // Log the data being passed to Hero
  useEffect(() => {
    console.log('Life passing to Hero:', {
      title: "Life",
      description: "Personal interests and creative pursuits",
      background: 'pinnacle'
    });
  }, []);

  const categories: Record<string, Category> = {
    music: {
      title: "MUSIC",
      subtitle: "Piano & More",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      )
    },
    photography: {
      title: "PHOTOGRAPHY", 
      subtitle: "Macro & Scenes",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    media: {
      title: "MEDIA",
      subtitle: "Video & Streaming",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    }
  };

  if (!metadata) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-300">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Hero 
        data={{
          title: "Life",
          description: "Personal interests and creative pursuits",
          background: 'pinnacle'
        }}
      />

      <div className="py-8 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-black/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-800"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Music Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="col-span-1"
              >
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-gray-800/50 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    {categories.music.icon}
                    <div>
                      <h2 className="text-xl font-bold text-white">{categories.music.title}</h2>
                      <p className="text-sm text-gray-400">{categories.music.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Instruments</h3>
                      <div className="flex flex-wrap gap-2">
                        {metadata.music.instruments.map((instrument) => (
                          <span
                            key={instrument}
                            className="px-3 py-1 border border-gray-800 rounded-full text-gray-300 text-sm bg-black/30"
                          >
                            {instrument}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Genres</h3>
                      <div className="flex flex-wrap gap-2">
                        {metadata.music.genres.map((genre) => (
                          <span
                            key={genre}
                            className="px-3 py-1 border border-gray-800 rounded-full text-gray-300 text-sm bg-black/30"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    </div>

                    {metadata.music.influences && (
                      <div>
                        <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Influences</h3>
                        <p className="text-sm text-gray-300">{metadata.music.influences}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Photography Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="col-span-1"
              >
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-gray-800/50 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    {categories.photography.icon}
                    <div>
                      <h2 className="text-xl font-bold text-white">{categories.photography.title}</h2>
                      <p className="text-sm text-gray-400">{categories.photography.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Equipment</h3>
                      <div className="flex flex-wrap gap-2">
                        {metadata.photography.equipment.map((item) => (
                          <span
                            key={item}
                            className="px-3 py-1 border border-gray-800 rounded-full text-gray-300 text-sm bg-black/30"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Styles</h3>
                      <div className="flex flex-wrap gap-2">
                        {metadata.photography.styles.map((style) => (
                          <span
                            key={style}
                            className="px-3 py-1 border border-gray-800 rounded-full text-gray-300 text-sm bg-black/30"
                          >
                            {style}
                          </span>
                        ))}
                      </div>
                    </div>

                    {metadata.photography.influences && (
                      <div>
                        <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Influences</h3>
                        <p className="text-sm text-gray-300">{metadata.photography.influences}</p>
                      </div>
                    )}

                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Portfolio</h3>
                      <div className="flex flex-col gap-2">
                        <a 
                          href="https://www.codymcox.photo" 
                          className="text-white hover:text-gray-300 transition-colors flex items-center gap-2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Website
                        </a>
                        <a 
                          href="https://instagram.com/codymcox.photo" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-white hover:text-gray-300 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          @codymcox.photo
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Media Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="col-span-1"
              >
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-gray-800/50 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    {categories.media.icon}
                    <div>
                      <h2 className="text-xl font-bold text-white">{categories.media.title}</h2>
                      <p className="text-sm text-gray-400">{categories.media.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Video</h3>
                      <div className="flex flex-wrap gap-2">
                        {metadata.media.video.map((item) => (
                          <span
                            key={item}
                            className="px-3 py-1 border border-gray-800 rounded-full text-gray-300 text-sm bg-black/30"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Platforms</h3>
                      <div className="flex flex-wrap gap-2">
                        {metadata.media.twitch.map((platform) => (
                          <span
                            key={platform}
                            className="px-3 py-1 border border-gray-800 rounded-full text-gray-300 text-sm bg-black/30"
                          >
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Life; 